import {
  ComposedChart,
  Line,
  ReferenceLine,
  ReferenceArea,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Scatter,
} from "recharts";
import type { SPCDataPoint, SPCStats, SPCSpecLimits, SPCAlert } from "@shared/types";
import { formatDate } from "@/lib/utils";

interface SPCChartProps {
  data: SPCDataPoint[];
  stats: SPCStats;
  spec: SPCSpecLimits;
  alerts?: SPCAlert[];
  testLabel: string;
  unit?: string;
}

export function SPCChart({ data, stats, spec, alerts = [], testLabel, unit = "" }: SPCChartProps) {
  if (data.length === 0) {
    return (
      <div className="flex h-80 items-center justify-center text-muted-foreground">
        No data for selected filters
      </div>
    );
  }

  // Build alert lookup by sample_id for fast rendering
  const alertsBySample = new Map<number, SPCAlert[]>();
  for (const a of alerts) {
    const list = alertsBySample.get(a.sample_id) || [];
    list.push(a);
    alertsBySample.set(a.sample_id, list);
  }

  // Determine Y-axis domain
  const values = data.map((d) => d.value).filter((v): v is number => v != null);
  const allLimits = [
    stats.ucl,
    stats.lcl,
    spec.error_high,
    spec.error_low,
    spec.warn_high,
    spec.warn_low,
    ...values,
  ].filter((v): v is number => v != null);

  const yMin = Math.floor(Math.min(...allLimits) * 0.95);
  const yMax = Math.ceil(Math.max(...allLimits) * 1.05);

  // Colour data points based on spec compliance
  const chartData = data.map((d) => {
    let fill = "hsl(142, 71%, 45%)"; // green — in spec
    if (d.value != null) {
      if (
        (spec.error_high != null && d.value > spec.error_high) ||
        (spec.error_low != null && d.value < spec.error_low)
      ) {
        fill = "hsl(0, 84%, 60%)"; // red — error
      } else if (
        (spec.warn_high != null && d.value > spec.warn_high) ||
        (spec.warn_low != null && d.value < spec.warn_low)
      ) {
        fill = "hsl(45, 93%, 47%)"; // amber — warning
      }
    }
    const hasAlert = alertsBySample.has(d.sample_id);
    return { ...d, fill, hasAlert };
  });

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-20" />

          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            className="text-muted-foreground"
            tickFormatter={(d) => {
              const date = new Date(d);
              return `${date.getDate()}/${date.getMonth() + 1}`;
            }}
          />
          <YAxis
            domain={[yMin, yMax]}
            tick={{ fontSize: 11 }}
            className="text-muted-foreground"
            label={{
              value: `${testLabel}${unit ? ` (${unit})` : ""}`,
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 11 },
            }}
          />

          {/* Warning zones */}
          {spec.warn_high != null && spec.error_high != null && (
            <ReferenceArea
              y1={spec.warn_high}
              y2={spec.error_high}
              fill="hsl(45, 93%, 47%)"
              fillOpacity={0.08}
            />
          )}
          {spec.warn_low != null && spec.error_low != null && (
            <ReferenceArea
              y1={spec.error_low}
              y2={spec.warn_low}
              fill="hsl(45, 93%, 47%)"
              fillOpacity={0.08}
            />
          )}

          {/* Error zones */}
          {spec.error_high != null && (
            <ReferenceArea
              y1={spec.error_high}
              y2={yMax}
              fill="hsl(0, 84%, 60%)"
              fillOpacity={0.06}
            />
          )}
          {spec.error_low != null && (
            <ReferenceArea
              y1={yMin}
              y2={spec.error_low}
              fill="hsl(0, 84%, 60%)"
              fillOpacity={0.06}
            />
          )}

          {/* Reference lines */}
          <ReferenceLine
            y={stats.mean}
            stroke="hsl(142, 71%, 45%)"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            label={{ value: `Mean: ${stats.mean}`, position: "right", fontSize: 10 }}
          />
          <ReferenceLine
            y={stats.ucl}
            stroke="hsl(0, 84%, 60%)"
            strokeDasharray="3 3"
            strokeWidth={1}
            label={{ value: `UCL: ${stats.ucl}`, position: "right", fontSize: 10 }}
          />
          <ReferenceLine
            y={stats.lcl}
            stroke="hsl(0, 84%, 60%)"
            strokeDasharray="3 3"
            strokeWidth={1}
            label={{ value: `LCL: ${stats.lcl}`, position: "right", fontSize: 10 }}
          />

          {/* Spec limits (solid) */}
          {spec.error_high != null && (
            <ReferenceLine
              y={spec.error_high}
              stroke="hsl(0, 84%, 60%)"
              strokeWidth={2}
            />
          )}
          {spec.error_low != null && (
            <ReferenceLine
              y={spec.error_low}
              stroke="hsl(0, 84%, 60%)"
              strokeWidth={2}
            />
          )}

          {/* Data line + dots */}
          <Line
            type="monotone"
            dataKey="value"
            stroke="hsl(220, 70%, 50%)"
            strokeWidth={1.5}
            dot={(props: Record<string, unknown>) => {
              const { cx, cy, payload } = props as {
                cx: number;
                cy: number;
                payload: { fill: string; hasAlert: boolean };
              };
              if (payload.hasAlert) {
                // Diamond shape for Nelson Rule flagged points
                const s = 5;
                return (
                  <g>
                    <polygon
                      points={`${cx},${cy - s} ${cx + s},${cy} ${cx},${cy + s} ${cx - s},${cy}`}
                      fill="hsl(280, 80%, 60%)"
                      stroke="hsl(280, 80%, 40%)"
                      strokeWidth={1.5}
                    />
                  </g>
                );
              }
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={3}
                  fill={payload.fill}
                  stroke={payload.fill}
                  strokeWidth={1}
                />
              );
            }}
            activeDot={{ r: 5, stroke: "hsl(220, 70%, 50%)", strokeWidth: 2 }}
          />

          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const d = payload[0].payload as SPCDataPoint & { fill: string; hasAlert: boolean };
              const sampleAlerts = alertsBySample.get(d.sample_id) || [];
              return (
                <div className="rounded-lg border border-border bg-popover p-3 text-sm text-popover-foreground shadow-md">
                  <p className="font-medium">{formatDate(d.date)}</p>
                  <p>
                    Value: <strong>{d.value}</strong>{unit ? ` ${unit}` : ""}
                  </p>
                  {d.sample_id && <p className="text-xs text-muted-foreground">Sample #{d.sample_id}</p>}
                  {d.operator && <p className="text-xs">Operator: {d.operator}</p>}
                  {d.run_number && <p className="text-xs">Run: {d.run_number}</p>}
                  {sampleAlerts.length > 0 && (
                    <div className="mt-2 border-t border-border pt-2">
                      {sampleAlerts.map((a, i) => (
                        <p key={i} className={`text-xs font-medium ${a.severity === "critical" ? "text-red-400" : "text-purple-400"}`}>
                          {a.description}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
